import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { postsAPI } from '../../services/api';
import PostCard from '../../components/PostCard/PostCard';
import { FiEdit3, FiLoader, FiPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import './MyPosts.css';

const MyPosts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    totalLikes: 0,
    totalComments: 0
  });

  useEffect(() => {
    loadMyPosts();
  }, [user?.id]);

  const loadMyPosts = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      
      // Obtener todas las publicaciones del usuario
      const response = await postsAPI.getAll({ author: user.id });
      const userPosts = response.data.posts || [];
      
      setPosts(userPosts);
      
      // Calcular estadísticas
      const totalLikes = userPosts.reduce((sum, post) => sum + (post.likes_count || 0), 0);
      const totalComments = userPosts.reduce((sum, post) => sum + (post.comments_count || 0), 0);
      
      setStats({
        total: userPosts.length,
        totalLikes,
        totalComments
      });
    } catch (err) {
      console.error('Error loading posts:', err);
      setError('Error al cargar tus publicaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPost = (postId) => {
    navigate(`/posts/edit/${postId}`);
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta publicación?')) {
      return;
    }

    try {
      await postsAPI.delete(postId);
      setPosts(posts.filter(post => post.id !== postId));
      setStats(prev => ({ ...prev, total: prev.total - 1 }));
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('Error al eliminar la publicación');
    }
  };

  const handleCreatePost = () => {
    navigate('/posts/create');
  };

  if (loading) {
    return (
      <div className="my-posts-container">
        <div className="my-posts-loading">
          <FiLoader className="spinning" size={32} />
          <p>Cargando tus publicaciones...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-posts-container">
        <div className="my-posts-error">
          <p>{error}</p>
          <button onClick={loadMyPosts} className="btn-retry">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // Check if user has permission to publish
  const canPublish = user && (
    user.can_publish || 
    ['superadmin', 'ceo', 'coo', 'head_of_content'].includes(user.hierarchical_role)
  );

  return (
    <div className="my-posts-container">
      <div className="my-posts-header">
        <div className="my-posts-title">
          <FiEdit3 size={28} />
          <h1>Mis Publicaciones</h1>
        </div>
        
        {canPublish && (
          <button onClick={handleCreatePost} className="btn-create-post">
            <FiPlus size={20} />
            Nueva Publicación
          </button>
        )}
      </div>

      <div className="my-posts-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Publicaciones</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.totalLikes}</div>
          <div className="stat-label">Me gusta totales</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.totalComments}</div>
          <div className="stat-label">Comentarios totales</div>
        </div>
      </div>

      {posts.length === 0 ? (
        <div className="my-posts-empty">
          <FiEdit3 size={64} />
          <h2>No tienes publicaciones aún</h2>
          <p>
            {canPublish ? (
              <>
                Comparte tu conocimiento con la comunidad.
                <br />
                ¡Crea tu primera publicación ahora!
              </>
            ) : (
              <>
                No tienes permisos para crear publicaciones.
                <br />
                Contacta a un administrador si necesitas publicar contenido.
              </>
            )}
          </p>
          {canPublish && (
            <button onClick={handleCreatePost} className="btn-create-post-large">
              <FiPlus size={20} />
              Crear Mi Primera Publicación
            </button>
          )}
        </div>
      ) : (
        <div className="my-posts-grid">
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              isOwnPost={true}
              onEdit={() => handleEditPost(post.id)}
              onDelete={() => handleDeletePost(post.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MyPosts;
